import React, { FC, useMemo } from 'react';
import { finishUpchargeCents } from 'shared/types/utils/finishUpcharge';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useI18n from 'helpers/hooks/useI18n';
import { textToColor } from 'helpers/textToColor/textToColor';
import { normalize } from 'helpers/utils/variantOrdering';
import { FinishGroup, groupFinishOptions } from '../helpers/finishGroups';
import { sortAxisValues } from '../helpers/optionAxes';

type FinishSwatchesProps = {
  /** Finish options as `"Name:#hex"` (or a bare name). */
  options: string[];
  /** Name of the selected finish. */
  selectedFinishName?: string;
  isWoodProduct: boolean;
  onSelect: (option: string) => void;
  /**
   * The selected finish's upcharge in whole cents at list price. Already multiplied per finish by
   * `useFinishAndSheens`, so only the sale factor is applied here.
   */
  upchargeCents?: number;
  discountFactor?: number;
  currencyCode?: string;
  fractionDigits?: number;
};

const FinishSwatches: FC<FinishSwatchesProps> = ({
  options,
  selectedFinishName,
  isWoodProduct,
  onSelect,
  upchargeCents,
  discountFactor = 1,
  currencyCode = 'USD',
  fractionDigits = 2,
}) => {
  const { locale } = useI18n();

  const groups = useMemo(
    () => groupFinishOptions(sortAxisValues('finish', options), isWoodProduct),
    [options, isWoodProduct],
  );

  // The upcharge is already the per-finish list amount; the sale factor is the only thing left to
  // apply, and it goes through the shared helper so this note can never disagree with the cart.
  const upcharge = finishUpchargeCents(upchargeCents, selectedFinishName, 1, discountFactor);

  const formattedUpcharge = CurrencyHelpers.formatForCurrency(
    { centAmount: upcharge, currencyCode, fractionDigits },
    locale,
  );

  const renderSwatch = (option: string) => {
    const color = textToColor(option);
    const selected = normalize(color.label) === normalize(selectedFinishName);

    return (
      <button
        key={`finish-${option}`}
        type="button"
        title={color.label}
        aria-label={color.label}
        aria-pressed={selected}
        onClick={() => onSelect(option)}
        style={{ backgroundColor: color.code }}
        // A ring rather than a border so selecting a swatch never nudges the row layout.
        className={`size-48 transition ${
          selected ? 'ring-2 ring-[#212121] ring-offset-2' : 'border border-[#d7d7d7] hover:border-[#212121]'
        }`}
      />
    );
  };

  /**
   * One row per group: the family name in a fixed left column, its swatches flowing in the
   * remaining width. Rows read as a table the eye can scan down, which the previous side-by-side
   * bands did not — those wrapped into uneven blocks whose grouping was hard to see.
   *
   * Only the gaps between rows are ruled. The step already has its own rule above and the next
   * element brings its own, so bounding this list as well double-lines both ends.
   */
  const renderGroup = (group: FinishGroup) => (
    <div
      key={group.title}
      // `md`, not `sm`: this project redefines sm as 320px, so an `sm:` row layout would apply on
      // every phone and leave a 160px label column eating half a 375px screen.
      className="flex flex-col gap-8 border-b border-[#e8e8e8] py-12 first:pt-0 last:border-b-0 last:pb-0 md:flex-row md:items-center md:gap-16"
    >
      {/* text-tertiary, not text-secondary: the latter maps to blue-dark in this token set. */}
      <p className="shrink-0 text-14 font-medium text-tertiary md:w-160">{group.title}</p>
      <div role="group" aria-label={group.title} className="flex flex-wrap gap-8">
        {group.options.map(renderSwatch)}
      </div>
    </div>
  );

  return (
    // Anchor for the mosaic PDP: the info panel releases its sticky when this row reaches the
    // medium tiles, so a finish change happens right beside the swatch it updates.
    <div data-finish-picker>
      {/*
        The rows need their own parent: `first:`/`last:` below target the first and last child,
        and the upcharge note is a sibling — flatten this and the note becomes the last child,
        leaving the final row's rule and padding in place.
      */}
      <div>{groups.map(renderGroup)}</div>

      {upcharge > 0 && (
        <p className="mt-12 text-12 text-tertiary">{`Selected finish adds ${formattedUpcharge} per item.`}</p>
      )}
    </div>
  );
};

export default FinishSwatches;
